import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Saved Campaign Service - Handles all operations related to saved/bookmarked campaigns
 */
const savedCampaignService = {
  /**
   * Save a campaign for an influencer (idempotent — safe to call even if already saved)
   * @param {string} influencerId - Influencer user ID
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object>} - Saved campaign object
   */
  saveCampaign: async (influencerId, campaignId) => {
    const docId = `${influencerId}_${campaignId}`;
    const savedCampaignRef = doc(db, 'savedCampaigns', docId);

    const savedCampaignData = {
      influencerId,
      campaignId,
      savedAt: serverTimestamp()
    };

    await setDoc(savedCampaignRef, savedCampaignData, { merge: true });

    return { id: docId, ...savedCampaignData };
  },

  /**
   * Unsave/remove a saved campaign (idempotent — safe to call even if not saved)
   * @param {string} influencerId - Influencer user ID
   * @param {string} campaignId - Campaign ID
   * @returns {Promise} - Promise that resolves when campaign is unsaved
   */
  unsaveCampaign: async (influencerId, campaignId) => {
    const docId = `${influencerId}_${campaignId}`;
    const savedCampaignRef = doc(db, 'savedCampaigns', docId);

    await deleteDoc(savedCampaignRef);
  },

  /**
   * Check if a campaign is saved by an influencer
   * @param {string} influencerId - Influencer user ID
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<boolean>} - True if campaign is saved, false otherwise
   */
  isCampaignSaved: async (influencerId, campaignId) => {
    const docId = `${influencerId}_${campaignId}`;
    const savedCampaignRef = doc(db, 'savedCampaigns', docId);

    const savedCampaignDoc = await getDoc(savedCampaignRef);
    return savedCampaignDoc.exists();
  },

  /**
   * Toggle saved status of a campaign.
   * Pass isCurrentlySaved from local state to avoid an extra Firestore read.
   * @param {string} influencerId - Influencer user ID
   * @param {string} campaignId - Campaign ID
   * @param {boolean} isCurrentlySaved - Whether the campaign is currently saved (from local state)
   * @returns {Promise<boolean>} - True if campaign is now saved, false if unsaved
   */
  toggleSavedCampaign: async (influencerId, campaignId, isCurrentlySaved = false) => {
    if (isCurrentlySaved) {
      await savedCampaignService.unsaveCampaign(influencerId, campaignId);
      return false;
    } else {
      await savedCampaignService.saveCampaign(influencerId, campaignId);
      return true;
    }
  },

  /**
   * Get all saved campaigns for an influencer
   * @param {string} influencerId - Influencer user ID
   * @returns {Promise<Array>} - Array of saved campaign objects with campaign data
   */
  getSavedCampaigns: async (influencerId) => {
    try {
      const savedCampaignsRef = collection(db, 'savedCampaigns');
      const q = query(
        savedCampaignsRef, 
        where('influencerId', '==', influencerId),
        orderBy('savedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      const savedCampaignsWithData = [];
      
      // Get campaign data for each saved campaign
      const campaignPromises = querySnapshot.docs.map(async (docSnap) => {
        const savedCampaign = docSnap.data();
        const campaignDoc = await getDoc(doc(db, 'campaigns', savedCampaign.campaignId));
        
        if (campaignDoc.exists()) {
          savedCampaignsWithData.push({
            id: docSnap.id,
            ...savedCampaign,
            campaign: { id: campaignDoc.id, ...campaignDoc.data() }
          });
        }
      });
      
      await Promise.all(campaignPromises);
      
      return savedCampaignsWithData;
    } catch (error) {
      console.error('Error getting saved campaigns:', error);
      throw error;
    }
  },

  /**
   * Get saved campaign IDs for an influencer
   * @param {string} influencerId - Influencer user ID
   * @returns {Promise<Array>} - Array of saved campaign IDs
   */
  getSavedCampaignIds: async (influencerId) => {
    try {
      const savedCampaignsRef = collection(db, 'savedCampaigns');
      const q = query(
        savedCampaignsRef, 
        where('influencerId', '==', influencerId)
      );
      
      const querySnapshot = await getDocs(q);
      
      const savedCampaignIds = [];
      querySnapshot.forEach((doc) => {
        savedCampaignIds.push(doc.data().campaignId);
      });
      
      return savedCampaignIds;
    } catch (error) {
      console.error('Error getting saved campaign IDs:', error);
      throw error;
    }
  }
};

export default savedCampaignService;
